import { apiClient } from '../client';

/**
 * Client for `/api/eos/todos/*`. Stays in lockstep with
 * `server/src/routes/eos/todos.routes.ts` (Stream B Wave 1, ADR-023).
 */

export type TodoScope = 'mine' | 'team' | 'all';
export type TodoStatusFilter = 'open' | 'done' | 'all';
export type SubtaskMode = 'parallel' | 'sequential';

export interface Todo {
  id: string;
  organizationId: string;
  title: string;
  description: string | null;
  ownerMembershipId: string | null;
  /** Owner display name, included server-side. */
  ownerMembership?: { id: string; user: { firstName: string; lastName: string } } | null;
  departmentId: string | null;
  dueDate: string;
  completedAt: string | null;
  /** How many weeks the item has been carried over (+7d each time). */
  carryCount: number;
  meetingId: string | null;
  issueId: string | null;
  rockId: string | null;
  createdByMembershipId: string | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** Null on top-level todos; set on subtasks (points at the parent). */
  parentTodoId: string | null;
  /** How this todo's own children run. Null when it has no subtasks. */
  subtaskMode: SubtaskMode | null;
  /** Position among siblings (0-based). */
  order: number;
  /** Present on TOP-LEVEL todos from `listTodos` — nested children, ordered. */
  subtasks?: Subtask[];
}

/**
 * A subtask is just a Todo with a parent, returned in a reduced shape nested
 * under its parent by `listTodos`. `blocked` is server-computed: always false
 * in parallel mode; in sequential mode it is true for every incomplete subtask
 * that is not the next-in-line one.
 */
export interface Subtask {
  id: string;
  title: string;
  description: string | null;
  ownerMembershipId: string | null;
  ownerMembership: { id: string; user: { firstName: string | null; lastName: string | null } } | null;
  dueDate: string;
  completedAt: string | null;
  order: number;
  blocked: boolean;
}

export interface ListTodosQuery {
  scope?: TodoScope;
  /** Required by the server when scope=team. */
  departmentId?: string;
  status?: TodoStatusFilter;
  includeArchived?: boolean;
  meetingId?: string;
}

export async function listTodos(q: ListTodosQuery = {}): Promise<Todo[]> {
  const params = new URLSearchParams();
  if (q.scope) params.set('scope', q.scope);
  if (q.departmentId) params.set('departmentId', q.departmentId);
  if (q.status) params.set('status', q.status);
  if (q.includeArchived) params.set('includeArchived', 'true');
  if (q.meetingId) params.set('meetingId', q.meetingId);
  const query = params.toString() ? `?${params.toString()}` : '';
  const res = await apiClient.get<{ data: Todo[] }>(`/api/eos/todos${query}`);
  return res.data.data;
}

export interface CreateTodoRequest {
  title: string;
  description?: string;
  /** Defaults to self server-side. Any active member is assignable (EOS convention). */
  ownerMembershipId?: string;
  /** ISO datetime with offset. Defaults to now + 7 days server-side. */
  dueDate?: string;
  departmentId?: string;
  rockId?: string;
  issueId?: string;
  /** Provenance — set when the to-do is created during an L10 meeting. */
  meetingId?: string;
}

export async function createTodo(body: CreateTodoRequest): Promise<Todo> {
  const res = await apiClient.post<{ data: Todo }>('/api/eos/todos', body);
  return res.data.data;
}

export interface UpdateTodoRequest {
  title?: string;
  description?: string | null;
  ownerMembershipId?: string;
  /** ISO datetime with offset. */
  dueDate?: string;
  departmentId?: string | null;
}

export async function updateTodo(id: string, body: UpdateTodoRequest): Promise<Todo> {
  const res = await apiClient.put<{ data: Todo }>(`/api/eos/todos/${encodeURIComponent(id)}`, body);
  return res.data.data;
}

/** Idempotent — completing an already-completed to-do is a no-op. */
export async function completeTodo(id: string): Promise<Todo> {
  const res = await apiClient.post<{ data: Todo }>(`/api/eos/todos/${encodeURIComponent(id)}/complete`);
  return res.data.data;
}

/** Push the due date +7 days and increment `carryCount`. */
export async function carryOverTodo(id: string): Promise<Todo> {
  const res = await apiClient.post<{ data: Todo }>(`/api/eos/todos/${encodeURIComponent(id)}/carry-over`);
  return res.data.data;
}

/** Soft delete — sets `archivedAt`. */
export async function archiveTodo(id: string): Promise<void> {
  await apiClient.delete(`/api/eos/todos/${encodeURIComponent(id)}`);
}

// ─────────────────────────────────────────────────────────────────────────
// Subtasks (ADR-023 Wave 2). A subtask is a Todo with a `parentTodoId`; it
// reuses complete/carry/archive/update. Only one level of nesting is allowed.
// ─────────────────────────────────────────────────────────────────────────

export interface CreateSubtaskRequest {
  title: string;
  /** Defaults to self server-side. */
  ownerMembershipId?: string;
  /** ISO datetime with offset. Defaults to the parent's due date server-side. */
  dueDate?: string;
}

/** Append a child to a parent todo. Returns the created child Todo. */
export async function createSubtask(parentId: string, body: CreateSubtaskRequest): Promise<Todo> {
  const res = await apiClient.post<{ data: Todo }>(
    `/api/eos/todos/${encodeURIComponent(parentId)}/subtasks`,
    body,
  );
  return res.data.data;
}

/** Switch a parent between parallel and sequential subtask execution. */
export async function setSubtaskMode(parentId: string, mode: SubtaskMode): Promise<Todo> {
  const res = await apiClient.put<{ data: Todo }>(
    `/api/eos/todos/${encodeURIComponent(parentId)}/subtask-mode`,
    { mode },
  );
  return res.data.data;
}

/** Persist a new sibling order (full ordered list of child ids). */
export async function reorderSubtasks(parentId: string, orderedIds: string[]): Promise<Todo> {
  const res = await apiClient.put<{ data: Todo }>(
    `/api/eos/todos/${encodeURIComponent(parentId)}/subtasks/order`,
    { orderedIds },
  );
  return res.data.data;
}
